<script setup>
import { ref, watch, onMounted } from "vue";
import DynamicReportForm from "@/components/DynamicReportForm.vue";
import { loadMyPermissions, hasAny } from "@/utils/permissions";
import Swal from "sweetalert2";
import api from "@/api";

// ===================== State =====================
const model = ref({
  customer_id: "",
  date_from: "",
  date_to: "",
});

const isLoading = ref(false);
const errors = ref({});

// ===================== Permissions =====================
const canView = ref(false);

onMounted(async () => {
  await loadMyPermissions(false);
  canView.value = hasAny(["customer_account_statements_report.view"]);
});

// Clear errors on change
watch(model, (v) => {
  if (v.customer_id) delete errors.value.customer_id;
  if (v.date_from)   delete errors.value.date_from;
  if (v.date_to)     delete errors.value.date_to;
}, { deep: true });

// ===================== Load customers =====================
async function loadCustomers() {
  const token = localStorage.getItem("token");
  if (!token) return [];

  try {
    const response = await api.get(`/customers`, {
      baseURL: `https://accounting.lyon-jo.com/api`,
      headers: { Authorization: `Bearer ${token}` },
    });

    const customers = Array.isArray(response.data)
      ? response.data
      : (response.data?.data ?? []);

    return customers.map((c) => ({
      label: c.full_name || c.name || `#${c.id}`,
      value: String(c.id),
    }));
  } catch (err) {
    console.error("Failed to load customers:", err);
    return [];
  }
}

// ===================== Validation =====================
function validate(p) {
  const e = {};
  if (!p.customer_id) e.customer_id = "Required.";
  if (!p.date_from)   e.date_from   = "Required.";
  if (!p.date_to)     e.date_to     = "Required.";

  if (p.date_from && p.date_to) {
    const from = new Date(p.date_from);
    const to   = new Date(p.date_to);
    if (isFinite(from) && isFinite(to) && to < from) {
      e.date_to = "Must be on or after Date From.";
    }
  }

  errors.value = e;
  return Object.keys(e).length === 0;
}

// ===================== Submit — blob fetch =====================
async function handleSubmit(p) {
  if (!canView.value || !validate(p)) return;

  const params = new URLSearchParams();
  if (p.customer_id) params.append("customer_id", p.customer_id);
  if (p.date_from)   params.append("from_date",   p.date_from);
  if (p.date_to)     params.append("to_date",     p.date_to);

  isLoading.value = true;
  try {
    const res = await api.get(
      `/accounting/reports/account-statement/pdf?${params.toString()}`,
      { responseType: "blob" }
    );
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    window.open(url, "_blank");
  } catch (err) {
    console.error("Failed to generate report:", err);
    Swal.fire({ icon: "error", title: "Failed to generate report", toast: true, position: "top", timer: 2000, showConfirmButton: false });
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <!-- 403 Gate -->
    <div
      v-if="!canView"
      class="mb-6 bg-white border border-gray-200 rounded-2xl shadow-sm p-10 text-center"
      role="alert"
    >
      <div class="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 text-red-600 grid place-items-center">
        <svg viewBox="0 0 24 24" class="w-6 h-6">
          <path fill="currentColor" d="M11 7h2v6h-2zm0 8h2v2h-2z"/>
          <path fill="currentColor" d="M1 21h22L12 2 1 21z"/>
        </svg>
      </div>
      <h2 class="font-bold text-gray-900 mb-1">403 – You don't have permission</h2>
      <p class="text-gray-600">Please contact the administrator to enable access.</p>
    </div>

    <!-- Main content -->
    <template v-else>
      <h1 class="text-[24px] font-bold text-gray-900 mb-12">
        Customer Account Statements
      </h1>

      <DynamicReportForm
        :showTitle="false"
        :columns="1"
        stackAt="never"
        contentWidth="xl"
        :loading="isLoading"
        :actionLabel="isLoading ? 'Generating…' : 'View Report'"
        :fields="[
          {
            name: 'customer_id',
            label: 'Customer Name',
            type: 'select',
            required: true,
            loader: loadCustomers,
            placeholder: 'Select Customer',
          },
          { name: 'date_from', label: 'Date From', type: 'date', required: true },
          { name: 'date_to',   label: 'Date To',   type: 'date', required: true },
        ]"
        :errors="errors"
        v-model="model"
        @submit="handleSubmit"
      />
    </template>
  </div>
</template>
